import React from 'react';

/**
 * StatusDot: 8-10px circle for list rows and rails.
 * Colours from the "Status dots" line in .kiro/steering/ui-design-system.md.
 * Colour alone never carries meaning: pass `label` so assistive tech
 * gets the state in words; without a label the dot is decorative.
 */
export type StatusDotTone = 'success' | 'warning' | 'danger' | 'idle' | 'running';

export const STATUS_DOT_CLASSES: Record<StatusDotTone, string> = {
  success: 'bg-[#16a34a]',
  warning: 'bg-[#eab308]',
  danger: 'bg-[#dc2626]',
  idle: 'bg-[#d1d5db]',
  running: 'bg-[#A34DF5] animate-pulse',
};

export interface StatusDotProps {
  tone?: StatusDotTone;
  /** 8 or 10 px. */
  size?: 8 | 10;
  /** Accessible name; also shown as a tooltip. */
  label?: string;
  className?: string;
}

export const StatusDot: React.FC<StatusDotProps> = ({
  tone = 'idle',
  size = 8,
  label,
  className = '',
}) => {
  const dimension = size === 10 ? 'h-2.5 w-2.5' : 'h-2 w-2';
  const a11y = label
    ? ({ role: 'img', 'aria-label': label, title: label } as const)
    : ({ 'aria-hidden': true } as const);
  return (
    <span
      {...a11y}
      data-tone={tone}
      className={`inline-block shrink-0 rounded-full ${dimension} ${STATUS_DOT_CLASSES[tone]} ${className}`.trim()}
    />
  );
};

export default StatusDot;
